import React, { useEffect, useRef, useState } from "react";
import { APP_VERSION } from "@/lib/version";

// Signup + Stripe subscription flow. Renders inside the shared `pa-auth-root`
// container (see Login.jsx) so it inherits the cream background + iOS safe-
// area padding without duplication.
//
// Three steps:
//   1. "register"  — collect pharmacy details, POST /api/auth/signup/register
//   2. "plan"      — show plan card, POST /api/auth/signup/checkout, redirect
//                    to Stripe Checkout
//   3. "success"   — after Stripe redirect back (?signup_session=<id>), poll
//                    /api/auth/signup/status until license PIN is minted;
//                    display PIN + CTA into Login/PIN pairing.
//
// Backend fee is defined server-side ($299.70/mo CAD). Frontend only frames
// the daily anchor for marketing copy.

const BACKEND = process.env.REACT_APP_BACKEND_URL;

async function api(path, opts = {}) {
  const res = await fetch(`${BACKEND}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
  });
  const text = await res.text();
  let json;
  try { json = text ? JSON.parse(text) : {}; } catch { json = { raw: text }; }
  if (!res.ok) {
    throw new Error(String(json.detail || json.error || `HTTP_${res.status}`));
  }
  return json;
}

// Format a North-American phone number progressively as the user types:
//   ""            → ""
//   "5"           → "(5"
//   "514"         → "(514"
//   "5145"        → "(514) 5"
//   "514555"      → "(514) 555"
//   "5145550110"  → "(514) 555-0110"
// Extra digits beyond 10 are dropped so the field can't overflow.
function formatPhone(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 10);
  if (!digits) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const initialForm = {
  pharmacy_name: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  fax: "",
  accreditation: "",
};

export default function Signup({ onCancel, onCompleted }) {
  // If we returned from Stripe with ?signup_session=..., jump straight to
  // the success poll instead of showing the empty form. Also carry an
  // optional ?signup_id so the poller can bind the PIN to the right doc.
  const [step, setStep] = useState(() => {
    if (typeof window === "undefined") return "register";
    const p = new URLSearchParams(window.location.search);
    return p.get("signup_session") ? "success" : "register";
  });
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [signupId, setSignupId] = useState("");
  const [issuedPin, setIssuedPin] = useState("");
  const [issuedPharmacy, setIssuedPharmacy] = useState("");
  const [pinCopied, setPinCopied] = useState(false);
  const [pollAttempt, setPollAttempt] = useState(0);

  const pollTimer = useRef(null);

  function setField(name, value) {
    // Auto-format phone / fax as user types → (XXX) XXX-XXXX
    if (name === "phone" || name === "fax") {
      value = formatPhone(value);
    }
    // Accreditation is a 5- or 6-digit numeric identifier (Ontario College of
    // Pharmacists format). Strip anything non-numeric and cap at 6 chars.
    if (name === "accreditation") {
      value = String(value || "").replace(/\D/g, "").slice(0, 6);
    }
    setForm((f) => ({ ...f, [name]: value }));
  }

  async function handleRegisterSubmit(e) {
    e.preventDefault();
    if (busy) return;
    setError("");
    // All fields are mandatory. Trim before checking so a whitespace-only
    // entry doesn't slip through.
    const required = [
      ["pharmacy_name", "Pharmacy name"],
      ["first_name", "First name"],
      ["last_name", "Last name"],
      ["email", "Email"],
      ["phone", "Phone"],
      ["fax", "Fax"],
      ["accreditation", "Accreditation number"],
    ];
    const missing = required.filter(([k]) => !String(form[k] || "").trim())
                            .map(([, label]) => label);
    if (missing.length) {
      setError(
        missing.length === required.length
          ? "Please fill in all fields."
          : `Please fill in: ${missing.join(", ")}.`
      );
      return;
    }
    // Phone / fax must be a full 10-digit NANP number (formatter caps at 10).
    const digits = (v) => String(v || "").replace(/\D/g, "");
    if (digits(form.phone).length !== 10) {
      setError("Please enter a valid 10-digit phone number.");
      return;
    }
    if (digits(form.fax).length !== 10) {
      setError("Please enter a valid 10-digit fax number.");
      return;
    }
    const acc = String(form.accreditation || "");
    if (!/^\d{5,6}$/.test(acc)) {
      setError("Accreditation number must be 5 or 6 digits.");
      return;
    }
    setBusy(true);
    try {
      const r = await api("/api/auth/signup/register", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setSignupId(r.signup_id);
      setStep("plan");
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setBusy(false);
    }
  }

  async function handleStartSubscription() {
    if (busy || !signupId) return;
    setError("");
    setBusy(true);
    try {
      const origin = window.location.origin;
      // We navigate away to Stripe. Include signup_id so the success return
      // page (which lives on our origin) can resume polling for THIS signup.
      const r = await api("/api/auth/signup/checkout", {
        method: "POST",
        body: JSON.stringify({ signup_id: signupId, origin_url: origin }),
      });
      if (!r.url) throw new Error("Stripe did not return a checkout URL.");
      // Stash signup_id in the return URL query so the success screen can
      // reconstitute after redirect (Stripe strips other query params).
      window.location.href = r.url;
    } catch (err) {
      setError(String(err.message || err));
      setBusy(false);
    }
  }

  // Poll the checkout status once we've landed on the success step.
  useEffect(() => {
    if (step !== "success") return;
    const p = new URLSearchParams(window.location.search);
    const sess = p.get("signup_session");
    if (!sess) {
      setError("Missing checkout session id — please contact support.");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const MAX = 20; // ~40s at 2s intervals

    async function tick() {
      if (cancelled) return;
      attempts += 1;
      setPollAttempt(attempts);
      try {
        const r = await api(`/api/auth/signup/status/${sess}`);
        if (r.status === "paid" && r.pin) {
          setIssuedPin(r.pin);
          setIssuedPharmacy(r.pharmacy_name || "");
          // Clean the querystring so a manual reload doesn't re-trigger polling.
          try {
            window.history.replaceState({}, "", window.location.pathname);
          } catch {
            // ignore — non-critical
          }
          return; // stop polling
        }
        if (r.status === "expired" || r.status === "cancelled") {
          setError("This checkout session expired. Please try again.");
          return;
        }
      } catch (err) {
        // Transient — keep polling until MAX
        if (attempts >= MAX) {
          setError("We couldn't confirm the payment. Please contact support.");
          return;
        }
      }
      if (attempts < MAX) {
        pollTimer.current = setTimeout(tick, 2000);
      } else {
        setError("Payment is still processing. Try refreshing in a moment.");
      }
    }
    tick();
    return () => {
      cancelled = true;
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, [step]);

  function copyPin() {
    if (!issuedPin) return;
    try {
      navigator.clipboard.writeText(issuedPin);
      setPinCopied(true);
      setTimeout(() => setPinCopied(false), 1600);
    } catch {
      // ignore — user can select text manually
    }
  }

  return (
    <>
      <style>{styles}</style>
      <div className="pa-topbar">
        <div className="pa-brand" data-testid="brand-wordmark">
          Pharmacy <em>Analyzer</em>
        </div>
        <div className="su-top-actions">
          <button
            type="button"
            className="su-back-pill"
            onClick={onCancel}
            data-testid="signup-back-btn"
          >
            ← Login
          </button>
          <span className="pa-ver">{APP_VERSION}</span>
        </div>
      </div>

      <div className="pa-body su-body">
        <StepDots active={step === "register" ? 0 : step === "plan" ? 1 : 2} />

        {step === "register" && (
          <RegisterStep
            form={form}
            setField={setField}
            busy={busy}
            error={error}
            onSubmit={handleRegisterSubmit}
          />
        )}

        {step === "plan" && (
          <PlanStep
            busy={busy}
            error={error}
            onStart={handleStartSubscription}
            onBack={() => setStep("register")}
          />
        )}

        {step === "success" && (
          <SuccessStep
            pin={issuedPin}
            pharmacyName={issuedPharmacy}
            pinCopied={pinCopied}
            onCopyPin={copyPin}
            onContinue={() => onCompleted?.({ pin: issuedPin })}
            error={error}
            attempts={pollAttempt}
          />
        )}
      </div>

      <div className="pa-foot">
        <strong>Processed in-memory.</strong><br />
        Your data is never stored on our servers.
      </div>
    </>
  );
}

function StepDots({ active }) {
  return (
    <div className="su-steps" data-testid="signup-step-dots">
      {[0, 1, 2].map((i) => (
        <span key={i} className={`su-step ${i === active ? "active" : ""}`} />
      ))}
    </div>
  );
}

function RegisterStep({ form, setField, busy, error, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="su-form" data-testid="signup-register-form">
      <div className="su-headline">
        Register your <em>pharmacy</em>
      </div>
      <div className="su-subhead">
        Tell us who you are — takes about 60 seconds.
      </div>

      <div className="su-scroll">
        <Field
          label="Pharmacy name"
          value={form.pharmacy_name}
          onChange={(v) => setField("pharmacy_name", v)}
          testid="signup-pharmacy-name"
          autoFocus
        />
        <div className="su-row2">
          <Field
            label="First name"
            value={form.first_name}
            onChange={(v) => setField("first_name", v)}
            testid="signup-first-name"
          />
          <Field
            label="Last name"
            value={form.last_name}
            onChange={(v) => setField("last_name", v)}
            testid="signup-last-name"
          />
        </div>
        <Field
          label="Email"
          value={form.email}
          onChange={(v) => setField("email", v)}
          type="email"
          testid="signup-email"
        />
        <Field
          label="Phone"
          value={form.phone}
          onChange={(v) => setField("phone", v)}
          type="tel"
          inputMode="tel"
          maxLength={14}
          testid="signup-phone"
        />
        <Field
          label="Fax"
          value={form.fax}
          onChange={(v) => setField("fax", v)}
          type="tel"
          inputMode="tel"
          maxLength={14}
          testid="signup-fax"
        />
        <Field
          label="Accreditation number"
          value={form.accreditation}
          onChange={(v) => setField("accreditation", v)}
          inputMode="numeric"
          maxLength={6}
          testid="signup-accreditation"
        />

        <button
          type="submit"
          className="su-cta"
          disabled={busy}
          data-testid="signup-register-continue-btn"
        >
          {busy ? "…" : "Continue to plan →"}
        </button>
        {error ? <div className="pa-error" data-testid="signup-error">{error}</div> : null}
      </div>
    </form>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid, autoFocus, inputMode, maxLength }) {
  return (
    <label className="su-field">
      <span className="su-label">{label}<span className="su-required" aria-hidden="true">*</span></span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="su-input"
        autoComplete="off"
        data-testid={testid}
        autoFocus={autoFocus}
        inputMode={inputMode}
        maxLength={maxLength}
        required
      />
    </label>
  );
}

function PlanStep({ busy, error, onStart, onBack }) {
  return (
    <div className="su-plan-wrap" data-testid="signup-plan-step">
      <div className="su-headline">
        Start your <em>subscription</em>
      </div>
      <div className="su-subhead">
        Auto-renews monthly. Cancel anytime.
      </div>

      <div className="su-plan">
        <div className="su-plan-name">Pharmacy Analyzer</div>
        <div className="su-plan-desc">
          For the cost of a daily dispensing fee, gain access to a robust
          operations dashboard that provides complete visibility into your
          pharmacy&rsquo;s performance. Our platform delivers audit-grade
          reporting and essential tools to drive growth and enhance patient
          care.
        </div>
        <div className="su-plan-price-row">
          <span className="su-plan-price">
            $9.99<small>/day CAD</small>
          </span>
          <span className="su-plan-billed">
            billed monthly<br />$299.70/mo
          </span>
        </div>
        <div className="su-plan-value">
          <div className="su-plan-value-title">
            Transform Your Pharmacy Operations with Our Comprehensive SaaS Solution
          </div>
        </div>
      </div>

      <div className="su-stripe-mark">
        Secured by Stripe · 256-bit TLS · PCI-DSS Level 1
      </div>

      <button
        type="button"
        className="su-cta"
        onClick={onStart}
        disabled={busy}
        data-testid="signup-start-subscription-btn"
      >
        {busy ? "Preparing checkout…" : "Start subscription · $9.99/day"}
      </button>
      <button
        type="button"
        className="pa-alt"
        onClick={onBack}
        disabled={busy}
        data-testid="signup-plan-back-btn"
      >
        ← Back
      </button>
      <div className="su-legal">
        By continuing you agree to be charged $299.70 CAD monthly.<br />
        Manage or cancel anytime from Settings.
      </div>
      {error ? <div className="pa-error" data-testid="signup-error">{error}</div> : null}
    </div>
  );
}

function SuccessStep({ pin, pharmacyName, pinCopied, onCopyPin, onContinue, error, attempts }) {
  const waiting = !pin && !error;
  return (
    <div className="su-success" data-testid="signup-success-step">
      {waiting ? (
        <>
          <div className="su-spinner" />
          <div className="su-headline">Confirming <em>payment</em>…</div>
          <div className="su-subhead">
            Stripe is finalising your subscription. This usually takes a few
            seconds{attempts > 3 ? " — hang tight." : "."}
          </div>
        </>
      ) : (
        <>
          <div className="su-check">
            <svg width="38" height="38" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                 strokeLinejoin="round" aria-hidden="true">
              <polyline points="4 12 10 18 20 6" />
            </svg>
          </div>
          <div className="su-headline">You&rsquo;re <em>in</em>.</div>
          <div className="su-subhead">
            Welcome to Pharmacy Analyzer{pharmacyName ? `, ${pharmacyName}` : ""}.<br />
            Your first month starts today.
          </div>

          <div className="su-pin-box" data-testid="signup-issued-pin">
            <div className="su-pin-label">Your license PIN</div>
            <div className="su-pin-value">{pin}</div>
            <button
              type="button"
              className="su-copy-btn"
              onClick={onCopyPin}
              data-testid="signup-copy-pin-btn"
            >
              {pinCopied ? "✓ Copied" : "Copy PIN"}
            </button>
          </div>

          <button
            type="button"
            className="su-cta"
            onClick={onContinue}
            data-testid="signup-continue-to-pair-btn"
          >
            Set up Face ID →
          </button>
          <div className="su-legal">
            Save this PIN somewhere safe. You&rsquo;ll need it to pair
            additional devices.
          </div>
        </>
      )}
      {error ? <div className="pa-error" data-testid="signup-error">{error}</div> : null}
    </div>
  );
}

const styles = `
.su-top-actions { display: inline-flex; align-items: center; gap: 8px; }
.su-back-pill {
  font-family: 'Geist','Inter';
  font-size: 10px;
  padding: 3px 9px;
  border-radius: 100px;
  font-weight: 600;
  background: rgba(93,74,40,0.10);
  color: #5D4A28;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.05s;
}
.su-back-pill:hover { opacity: 0.85; }
.su-back-pill:active { transform: scale(0.96); }

.su-body {
  /* Signup is form-heavy — the entire body scrolls (not just the field
     list). Making the whole panel a scroll container means the headline
     and step-dots also scroll away as the user drills down, freeing
     real estate for the inputs — especially when the iOS keyboard is
     open. touch-action: pan-y locks momentum scrolling to the vertical
     axis so we do not fight the parent rubber-band lock. */
  justify-content: flex-start !important;
  padding-top: 24px !important;
  padding-bottom: 24px !important;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  overscroll-behavior: contain;
}
.su-steps {
  display: flex; gap: 6px; justify-content: center; margin-bottom: 22px;
  flex-shrink: 0;
}
.su-step {
  width: 6px; height: 6px; border-radius: 50%;
  background: rgba(27,59,46,0.18);
  transition: width 0.2s ease, background 0.2s ease;
}
.su-step.active {
  width: 22px; border-radius: 3px; background: #2B5E4A;
}

.su-headline {
  font-family: 'Fraunces', serif;
  font-size: 26px;
  font-weight: 400;
  letter-spacing: -0.02em;
  line-height: 1.12;
  color: #1B3B2E;
  text-align: center;
  margin-bottom: 10px;
}
.su-headline em { font-style: italic; color: #2B5E4A; font-weight: 400; }
.su-subhead {
  font-size: 13px;
  color: #5D4A28;
  line-height: 1.55;
  text-align: center;
  margin: 0 auto 22px;
  max-width: 320px;
}

.su-form { display: contents; }
.su-scroll {
  /* Historical inner scroll container — now a plain wrapper. The parent
     .su-body is the actual scroll region, so this div just groups the
     fields without imposing its own overflow rules (which on iOS Safari
     conflicted with the outer scroll and made large forms feel stuck). */
  width: 100%;
  padding: 0 2px 8px;
}
.su-field { display: flex; flex-direction: column; margin-bottom: 10px; width: 100%; text-align: left; }
.su-label {
  font-size: 10px;
  color: #5D4A28;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.9px;
  font-weight: 500;
  text-align: left;
}
.su-required {
  color: #862F1A;
  margin-left: 3px;
  font-weight: 600;
}
.su-input {
  background: #FFFDF7;
  border: 1px solid rgba(27,59,46,0.18);
  border-radius: 10px;
  padding: 9px 12px;
  /* IMPORTANT: 16px is the iOS-Safari-safe minimum. Anything smaller
     triggers a full-page auto-zoom the moment the user taps the input,
     and iOS often refuses to zoom back out afterward. Do NOT lower. */
  font-size: 16px;
  color: #1B3B2E;
  font-family: inherit;
  width: 100%;
  outline: none;
  transition: border-color 0.15s;
  text-align: left;
}
.su-input:focus {
  border-color: #2B5E4A;
  box-shadow: 0 0 0 3px rgba(43,94,74,0.14);
}
.su-row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }

.su-cta {
  background: #1B3B2E;
  color: #FBF6E9;
  border: none;
  padding: 14px 22px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 100px;
  width: 100%;
  cursor: pointer;
  font-family: inherit;
  margin-top: 6px;
  transition: opacity 0.15s, transform 0.05s;
}
.su-cta:hover:not(:disabled) { opacity: 0.9; }
.su-cta:active:not(:disabled) { transform: scale(0.98); }
.su-cta:disabled { opacity: 0.55; cursor: default; }

.su-plan-wrap { width: 100%; }
.su-plan {
  border: 1px solid rgba(27,59,46,0.14);
  border-radius: 16px;
  padding: 16px 18px;
  background: rgba(43,94,74,0.04);
  margin-bottom: 14px;
  text-align: left;
}
.su-plan-name {
  font-family: 'Fraunces', serif;
  font-style: italic;
  font-size: 17px;
  margin-bottom: 4px;
}
.su-plan-desc {
  font-size: 11.5px;
  color: #5D4A28;
  line-height: 1.5;
}
.su-plan-price-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: 12px 0 8px;
}
.su-plan-price {
  font-family: 'Geist', sans-serif;
  font-weight: 600;
  font-size: 22px;
  color: #2B5E4A;
}
.su-plan-price small {
  font-size: 11px;
  color: #5D4A28;
  font-weight: 400;
  margin-left: 3px;
}
.su-plan-billed {
  font-size: 10.5px;
  color: #7A6947;
  text-align: right;
  line-height: 1.4;
}
.su-plan-value {
  background: rgba(93,74,40,0.10);
  border-radius: 12px;
  padding: 12px 14px;
  margin-top: 12px;
}
.su-plan-value-title {
  font-family: 'Fraunces', serif;
  font-style: italic;
  font-weight: 500;
  color: #1B3B2E;
  font-size: 13.5px;
  line-height: 1.3;
}
.su-stripe-mark {
  text-align: center;
  font-size: 9.5px;
  color: #7A6947;
  margin: 4px 0 14px;
  letter-spacing: 0.6px;
}
.su-legal {
  font-size: 10.5px;
  color: #7A6947;
  text-align: center;
  margin-top: 10px;
  line-height: 1.55;
}

.su-success { text-align: center; width: 100%; }
.su-check {
  width: 76px; height: 76px;
  border-radius: 50%;
  background: rgba(43,94,74,0.12);
  display: flex; align-items: center; justify-content: center;
  margin: 20px auto 20px;
  color: #2B5E4A;
}
.su-spinner {
  width: 42px; height: 42px;
  margin: 30px auto 22px;
  border-radius: 50%;
  border: 3px solid rgba(43,94,74,0.15);
  border-top-color: #2B5E4A;
  animation: su-spin 0.8s linear infinite;
}
@keyframes su-spin { to { transform: rotate(360deg); } }

.su-pin-box {
  border: 1px dashed rgba(43,94,74,0.35);
  background: rgba(43,94,74,0.05);
  border-radius: 14px;
  padding: 14px 18px;
  margin: 18px auto;
  max-width: 320px;
}
.su-pin-label {
  font-size: 10px;
  color: #5D4A28;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  margin-bottom: 4px;
}
.su-pin-value {
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 20px;
  color: #1B3B2E;
  letter-spacing: 0.12em;
  font-weight: 600;
  margin-bottom: 8px;
  user-select: all;
  -webkit-user-select: all;
}
.su-copy-btn {
  background: transparent;
  border: 1px solid rgba(27,59,46,0.22);
  color: #1B3B2E;
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 100px;
  cursor: pointer;
  transition: background 0.15s;
}
.su-copy-btn:hover { background: rgba(27,59,46,0.06); }
`;
